import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Badge,
  LinearProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AccountCircle,
  Business,
  Payment,
  Notifications,
  Security,
  Schedule,
  Language,
  Edit,
  Save,
  CloudUpload,
  Delete,
  Visibility,
  VisibilityOff,
  CreditCard,
  AccountBalance,
  Receipt,
  CheckCircle,
  Warning,
  Info,
  Star,
  StarBorder,
  Description,
  Store,
  History,
  CardMembership,
  Loop,
  Cancel,
  Upgrade,
  ReceiptLong,
  CheckCircleOutline,
  TrendingUp,
  ArrowDownward,
  Support,
  Email,
  Phone,
  Chat,
  Download,
  Print,
  Share,
  Favorite,
  FavoriteBorder,
  Compare,
  ExpandMore,
  ExpandLess,
  Lock,
  Description as DescriptionIcon,
  Close,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { updateDoctorProfile } from '../../features/doctors/profileSlice';
import { updateClinicProfile } from '../../features/clinics/clinicProfileSlice';
import { useRazorpay } from 'react-razorpay';
import { useSnackbar } from 'notistack';

// Import our new subscription components
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import SubscriptionAwareComponent from '../../components/SubscriptionAwareComponent';
import SubscriptionSettings from './SubscriptionSettings';
import SubscriptionManagement from '../../components/SubscriptionManagement';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const ProfileCompletionCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const requiredFields = [
  'name', 'gender', 'age', 'city', 'country', 'consultation_fee',
  'bank_name', 'account_holder_name', 'account_number', 'ifsc_code'
];

const ProfileTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const photoInputRef = React.useRef(null);

  // Fetch profile with retry logic
  const fetchProfile = React.useCallback(async (retries = 3) => {
    setLoadingProfile(true);
    setProfileError(null);
    try {
      const res = await axiosInstance.get('/doctors/doctors/me/');
      setDoctorProfile(res.data);
      const formData = { ...res.data };
      setForm(formData);
      setOriginalForm(formData);
      setRetryCount(0);
      setFormErrors({});
    } catch (err) {
      console.error('Error fetching profile:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to load profile';
      
      // Retry on network/server errors
      if (retries > 0 && (!err.response || err.response.status >= 500)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchProfile(retries - 1), 2000);
        return;
      }
      
      setProfileError(errorMessage);
      // Don't show snackbar for 404 (might be onboarding required)
      if (err.response?.status !== 404) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setLoadingProfile(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) return null;
    // Allow digits, spaces, dashes, parentheses, plus
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(phone)) {
      return 'Phone number contains invalid characters';
    }
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return 'Phone number must be between 10 and 15 digits';
    }
    return null;
  };

  const validateAge = (age) => {
    if (!age) return null;
    const ageNum = parseInt(age);
    if (isNaN(ageNum) || ageNum < 18 || ageNum > 120) {
      return 'Age must be between 18 and 120';
    }
    return null;
  };

  const validateConsultationFee = (fee) => {
    if (!fee) return null;
    const feeNum = parseFloat(fee);
    if (isNaN(feeNum) || feeNum < 0 || feeNum > 100000) {
      return 'Consultation fee must be between 0 and 100,000';
    }
    return null;
  };

  const validateExperience = (exp) => {
    if (!exp) return null;
    const expNum = parseInt(exp);
    if (isNaN(expNum) || expNum < 0 || expNum > 100) {
      return 'Experience must be between 0 and 100 years';
    }
    return null;
  };

  const validateField = (field, value) => {
    let error = null;
    switch (field) {
      case 'phone':
        error = validatePhone(value);
        break;
      case 'age':
        error = validateAge(value);
        break;
      case 'consultation_fee':
        error = validateConsultationFee(value);
        break;
      case 'experience':
        error = validateExperience(value);
        break;
      case 'name':
        if (!value || value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        } else if (value.trim().length > 100) {
          error = 'Name must be less than 100 characters';
        }
        break;
      case 'mci_registration_number':
        if (value && value.length > 20) {
          error = 'MCI registration number must be less than 20 characters';
        }
        break;
      case 'rci_registration_number':
        if (value && value.length > 20) {
          error = 'RCI registration number must be less than 20 characters';
        }
        break;
      default:
        break;
    }
    setFormErrors(prev => ({ ...prev, [field]: error }));
    return error === null;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Validate on change
      validateField(field, value);
      return updated;
    });
  };

  const handleCancel = () => {
    setForm(originalForm);
    setFormErrors({});
    setEditMode(false);
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate required fields
    if (!form.name || !form.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (!validateField('name', form.name)) {
      isValid = false;
    }

    if (form.phone && !validateField('phone', form.phone)) {
      isValid = false;
    }

    if (form.age && !validateField('age', form.age)) {
      isValid = false;
    }

    if (form.consultation_fee !== undefined && form.consultation_fee !== null && form.consultation_fee !== '' && !validateField('consultation_fee', form.consultation_fee)) {
      isValid = false;
    }

    if (form.experience !== undefined && form.experience !== null && form.experience !== '' && !validateField('experience', form.experience)) {
      isValid = false;
    }

    if (form.mci_registration_number && !validateField('mci_registration_number', form.mci_registration_number)) {
      isValid = false;
    }

    if (form.rci_registration_number && !validateField('rci_registration_number', form.rci_registration_number)) {
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    // Validate form before saving
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors before saving', { variant: 'error' });
      return;
    }

    setSavingProfile(true);
    setProfileError(null);

    try {
      // Format data for backend - convert arrays to proper format for SlugRelatedFields
      const submitData = { ...form };
      
      // Handle specialities, qualifications, languages - convert to array of strings (names)
      if (submitData.specialities) {
        if (typeof submitData.specialities === 'string') {
          // Comma-separated string from input
          submitData.specialities = submitData.specialities.split(',').map(s => s.trim()).filter(Boolean);
        } else if (Array.isArray(submitData.specialities)) {
          submitData.specialities = submitData.specialities.map(s => typeof s === 'object' ? s.name : s).filter(Boolean);
        }
      }
      
      if (submitData.qualifications) {
        if (typeof submitData.qualifications === 'string') {
          submitData.qualifications = submitData.qualifications.split(',').map(q => q.trim()).filter(Boolean);
        } else if (Array.isArray(submitData.qualifications)) {
          submitData.qualifications = submitData.qualifications.map(q => typeof q === 'object' ? q.name : q).filter(Boolean);
        }
      }
      
      if (submitData.languages) {
        if (typeof submitData.languages === 'string') {
          submitData.languages = submitData.languages.split(',').map(l => l.trim()).filter(Boolean);
        } else if (Array.isArray(submitData.languages)) {
          submitData.languages = submitData.languages.map(l => typeof l === 'object' ? l.name : l).filter(Boolean);
        }
      }

      // Handle consultation_types and availability_days if they're strings
      if (submitData.consultation_types && typeof submitData.consultation_types === 'string') {
        submitData.consultation_types = submitData.consultation_types.split(',').map(t => t.trim()).filter(Boolean);
      }
      if (submitData.availability_days && typeof submitData.availability_days === 'string') {
        submitData.availability_days = submitData.availability_days.split(',').map(d => d.trim()).filter(Boolean);
      }
      
      // Remove nested objects that shouldn't be sent
      delete submitData.user;
      delete submitData.clinic;
      delete submitData.ratings;
      delete submitData.verification;
      delete submitData.average_rating;
      delete submitData.id; // Don't send ID in PUT
      
      const response = await axiosInstance.put('/doctors/doctors/me/', submitData, {
        timeout: 30000, // 30 second timeout
      });
      
      enqueueSnackbar('Profile updated successfully', { variant: 'success' });
      setDoctorProfile(response.data || form);
      setForm(response.data || form);
      setOriginalForm(response.data || form);
      setEditMode(false);
      setProfileError(null);
      setFormErrors({});
    } catch (err) {
      console.error('Save error:', err);
      
      let errorMessage = 'Failed to update profile';
      if (err.response?.data) {
        // Handle validation errors from backend
        if (err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (typeof err.response.data === 'object') {
          // Field-specific errors
          const fieldErrors = Object.entries(err.response.data).map(([field, errors]) => {
            return `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`;
          }).join('; ');
          errorMessage = fieldErrors || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Handle specific error cases
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.response.status === 403) {
        errorMessage = 'You do not have permission to update this profile.';
      } else if (err.response.status === 404) {
        errorMessage = 'Profile not found. Please complete your profile setup first.';
      }

      setProfileError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      enqueueSnackbar('Photo size must be less than 5MB', { variant: 'error' });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Photo must be a JPG or PNG image', { variant: 'error' });
      return;
    }

    setSavingProfile(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await axiosInstance.patch('/doctors/doctors/me/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      enqueueSnackbar('Photo updated successfully', { variant: 'success' });
      setDoctorProfile(response.data || form);
      setForm(response.data || form);
      setOriginalForm(response.data || form);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || 'Failed to upload photo';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingProfile(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  // Profile completion calculation
  const completion = doctorProfile ? Math.round(
    (requiredFields.filter(f => doctorProfile[f]).length / requiredFields.length) * 100
  ) : 0;

  if (loadingProfile) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  if (profileError && !doctorProfile && retryCount >= 3) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {profileError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setRetryCount(0);
            fetchProfile();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>Profile Information</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete your profile to help patients find you and book appointments.
          </Typography>
          <ProfileCompletionCard>
            <Typography variant="subtitle2" gutterBottom>Profile Completion</Typography>
            <LinearProgress 
              variant="determinate" 
              value={completion} 
              sx={{ height: 10, borderRadius: 5, mb: 1 }} 
            />
            <Typography variant="body2">{completion}% Complete</Typography>
          </ProfileCompletionCard>
        </Box>

        {profileError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError(null)}>
            {profileError}
          </Alert>
        )}

        <Box component="form" noValidate autoComplete="off">
          <Grid container spacing={3}>
            {/* Photo Upload Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={form.photo}
                  sx={{ width: 100, height: 100 }}
                >
                  {form.name ? form.name.charAt(0).toUpperCase() : 'D'}
                </Avatar>
                <Box>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                    id="photo-upload-input"
                  />
                  <label htmlFor="photo-upload-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<AccountCircle />}
                      disabled={savingProfile}
                    >
                      {savingProfile ? 'Uploading...' : 'Change Photo'}
                    </Button>
                  </label>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    JPG or PNG, max 5MB
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name *"
                value={form.name || ''}
                onChange={e => handleChange('name', e.target.value)}
                fullWidth
                required
                disabled={!editMode || savingProfile}
                error={!!formErrors.name}
                helperText={formErrors.name || 'Your full name as you want it displayed'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                value={form.phone || ''}
                onChange={e => handleChange('phone', e.target.value)}
                fullWidth
                disabled={!editMode || savingProfile}
                error={!!formErrors.phone}
                helperText={formErrors.phone || 'Contact phone number'}
              />
            </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Gender"
              value={form.gender || ''}
              onChange={e => handleChange('gender', e.target.value)}
              select
              SelectProps={{ native: true }}
              fullWidth
              disabled={!editMode || savingProfile}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Age"
              type="number"
              value={form.age || ''}
              onChange={e => handleChange('age', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
              error={!!formErrors.age}
              helperText={formErrors.age || 'Your age in years'}
              inputProps={{ min: 18, max: 120 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              value={form.city || ''}
              onChange={e => handleChange('city', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              value={form.state || ''}
              onChange={e => handleChange('state', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Country"
              value={form.country || ''}
              onChange={e => handleChange('country', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              value={form.address || ''}
              onChange={e => handleChange('address', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Experience (years)"
              type="number"
              value={form.experience || ''}
              onChange={e => handleChange('experience', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
              error={!!formErrors.experience}
              helperText={formErrors.experience || 'Years of professional experience'}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Doctor Type"
              value={form.doctor_type || ''}
              onChange={e => handleChange('doctor_type', e.target.value)}
              select
              SelectProps={{ native: true }}
              fullWidth
              disabled={!editMode || savingProfile}
            >
              <option value="">Select Type</option>
              <option value="psychiatrist">Psychiatrist</option>
              <option value="clinical_psychologist">Clinical Psychologist</option>
              <option value="counseling_psychologist">Counseling Psychologist</option>
              <option value="psychotherapist">Psychotherapist</option>
              <option value="other">Other</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Consultation Fee"
              type="number"
              value={form.consultation_fee || ''}
              onChange={e => handleChange('consultation_fee', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
              error={!!formErrors.consultation_fee}
              helperText={formErrors.consultation_fee || 'Fee in Indian Rupees'}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100000, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="MCI Registration Number"
              value={form.mci_registration_number || ''}
              onChange={e => handleChange('mci_registration_number', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
              error={!!formErrors.mci_registration_number}
              helperText={formErrors.mci_registration_number || 'Medical Council of India registration'}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="RCI Registration Number"
              value={form.rci_registration_number || ''}
              onChange={e => handleChange('rci_registration_number', e.target.value)}
              fullWidth
              disabled={!editMode || savingProfile}
              error={!!formErrors.rci_registration_number}
              helperText={formErrors.rci_registration_number || 'Rehabilitation Council of India registration'}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Specialities"
              value={form.specialities?.map(s => typeof s === 'object' ? s.name : s).join(', ') || ''}
              onChange={e => handleChange('specialities', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              fullWidth
              disabled={!editMode || savingProfile}
              helperText="Comma separated specialities (e.g., Psychiatry, Clinical Psychology)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Qualifications"
              value={form.qualifications?.map(q => typeof q === 'object' ? q.name : q).join(', ') || ''}
              onChange={e => handleChange('qualifications', e.target.value.split(',').map(q => q.trim()).filter(Boolean))}
              fullWidth
              disabled={!editMode || savingProfile}
              helperText="Comma separated qualifications (e.g., MBBS, MD, PhD)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Languages"
              value={form.languages?.map(l => typeof l === 'object' ? l.name : l).join(', ') || ''}
              onChange={e => handleChange('languages', e.target.value.split(',').map(l => l.trim()).filter(Boolean))}
              fullWidth
              disabled={!editMode || savingProfile}
              helperText="Comma separated languages you speak (e.g., English, Hindi, Tamil)"
            />
          </Grid>
        </Grid>
          <Box mt={3} sx={{ display: 'flex', gap: 2 }}>
            {editMode ? (
              <>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSave}
                  disabled={savingProfile}
                  startIcon={savingProfile ? <CircularProgress size={20} /> : <Save />}
                >
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleCancel}
                  disabled={savingProfile}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                variant="contained" 
                onClick={() => setEditMode(true)}
                startIcon={<Edit />}
              >
                Edit Profile
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const ClinicTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [clinicProfile, setClinicProfile] = useState(null);
  const [loadingClinic, setLoadingClinic] = useState(true);
  const [savingClinic, setSavingClinic] = useState(false);
  const [clinicError, setClinicError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [clinicEditMode, setClinicEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Fetch clinic profile with retry logic
  const fetchClinic = React.useCallback(async (retries = 3) => {
    setLoadingClinic(true);
    setClinicError(null);
    try {
      const res = await axiosInstance.get('/clinics/profile/');
      setClinicProfile(res.data);
      const formData = { ...res.data };
      setForm(formData);
      setOriginalForm(formData);
      setRetryCount(0);
      setFormErrors({});
    } catch (err) {
      console.error('Error fetching clinic profile:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to load clinic profile';
      
      // Retry on network/server errors
      if (retries > 0 && (!err.response || err.response.status >= 500)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchClinic(retries - 1), 2000);
        return;
      }
      
      setClinicError(errorMessage);
      // Don't show snackbar for 404 (clinic might not exist yet)
      if (err.response?.status !== 404) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setLoadingClinic(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchClinic();
  }, [fetchClinic]);

  // Validation functions
  const validateEmail = (email) => {
    if (!email) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePhone = (phone) => {
    if (!phone) return null;
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(phone)) {
      return 'Phone number contains invalid characters';
    }
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      return 'Phone number must be between 10 and 15 digits';
    }
    return null;
  };

  const validateField = (field, value) => {
    let error = null;
    switch (field) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone_number':
        error = validatePhone(value);
        break;
      case 'clinic_name':
        if (value && value.trim().length < 2) {
          error = 'Clinic name must be at least 2 characters';
        } else if (value && value.trim().length > 200) {
          error = 'Clinic name must be less than 200 characters';
        }
        break;
      default:
        break;
    }
    setFormErrors(prev => ({ ...prev, [field]: error }));
    return error === null;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      validateField(field, value);
      return updated;
    });
  };

  const handleCancel = () => {
    setForm(originalForm);
    setFormErrors({});
    setClinicEditMode(false);
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (form.email && !validateField('email', form.email)) {
      isValid = false;
    }
    if (form.phone_number && !validateField('phone_number', form.phone_number)) {
      isValid = false;
    }
    if (form.clinic_name && !validateField('clinic_name', form.clinic_name)) {
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors before saving', { variant: 'error' });
      return;
    }

    setSavingClinic(true);
    setClinicError(null);

    try {
      const response = await axiosInstance.put('/clinics/profile/', form, {
        timeout: 30000,
      });
      
      enqueueSnackbar('Clinic profile updated successfully', { variant: 'success' });
      setClinicProfile(response.data || form);
      setForm(response.data || form);
      setOriginalForm(response.data || form);
      setClinicEditMode(false);
      setClinicError(null);
      setFormErrors({});
    } catch (err) {
      console.error('Save error:', err);
      
      let errorMessage = 'Failed to update clinic profile';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (typeof err.response.data === 'object') {
          const fieldErrors = Object.entries(err.response.data).map(([field, errors]) => {
            return `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`;
          }).join('; ');
          errorMessage = fieldErrors || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.response.status === 403) {
        errorMessage = 'You do not have permission to update this clinic profile.';
      } else if (err.response.status === 404) {
        errorMessage = 'Clinic profile not found.';
      }

      setClinicError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingClinic(false);
    }
  };

  if (loadingClinic) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading clinic profile...
        </Typography>
      </Box>
    );
  }

  if (clinicError && !clinicProfile && retryCount >= 3) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {clinicError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setRetryCount(0);
            fetchClinic();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>Clinic Information</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage your clinic profile and contact information.
        </Typography>
      </Box>

      {clinicError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClinicError(null)}>
          {clinicError}
        </Alert>
      )}

      <Box component="form" noValidate autoComplete="off">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label="Clinic Name"
              value={form.clinic_name || ''}
              onChange={e => handleChange('clinic_name', e.target.value)}
              fullWidth
              disabled={!clinicEditMode || savingClinic}
              error={!!formErrors.clinic_name}
              helperText={formErrors.clinic_name || 'Name of your clinic or practice'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              value={form.address || ''}
              onChange={e => handleChange('address', e.target.value)}
              fullWidth
              disabled={!clinicEditMode || savingClinic}
              multiline
              rows={2}
              helperText="Full address of your clinic"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone Number"
              value={form.phone_number || ''}
              onChange={e => handleChange('phone_number', e.target.value)}
              fullWidth
              disabled={!clinicEditMode || savingClinic}
              error={!!formErrors.phone_number}
              helperText={formErrors.phone_number || 'Clinic contact phone number'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              type="email"
              value={form.email || ''}
              onChange={e => handleChange('email', e.target.value)}
              fullWidth
              disabled={!clinicEditMode || savingClinic}
              error={!!formErrors.email}
              helperText={formErrors.email || 'Clinic contact email address'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Working Hours"
              value={form.working_hours || ''}
              onChange={e => handleChange('working_hours', e.target.value)}
              fullWidth
              disabled={!clinicEditMode || savingClinic}
              helperText="e.g., Mon-Fri: 9 AM - 6 PM, Sat: 9 AM - 1 PM"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Services Offered"
              value={form.services_offered || ''}
              onChange={e => handleChange('services_offered', e.target.value)}
              fullWidth
              disabled={!clinicEditMode || savingClinic}
              helperText="List of services your clinic offers"
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
        
        <Box mt={3} sx={{ display: 'flex', gap: 2 }}>
          {clinicEditMode ? (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={savingClinic}
                startIcon={savingClinic ? <CircularProgress size={20} /> : <Save />}
              >
                {savingClinic ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleCancel}
                disabled={savingClinic}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button 
              variant="contained" 
              onClick={() => setClinicEditMode(true)}
              startIcon={<Edit />}
            >
              Edit Clinic Profile
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const DocumentsTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [onboardingId, setOnboardingId] = useState(null);
  const [documents, setDocuments] = useState({});
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [docsError, setDocsError] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [docType, setDocType] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const fileInputRef = React.useRef(null);

  // Document type options
  const documentTypes = [
    { value: 'identity_proof', label: 'Identity Proof', required: true },
    { value: 'qualification', label: 'Qualification Certificate', required: true },
    { value: 'registration', label: 'Registration Certificate', required: true },
    { value: 'experience', label: 'Experience Certificate', required: false },
    { value: 'specialization', label: 'Specialization Certificate', required: false },
    { value: 'other', label: 'Other Document', required: false },
  ];

  // Constants for file validation
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

  // Fetch documents with retry logic
  const fetchDocuments = React.useCallback(async (retries = 3) => {
    try {
      setLoadingDocs(true);
      setDocsError(null);

      // Get onboarding ID
      const onboardingRes = await axiosInstance.get('/doctors/onboarding/');
      const id = onboardingRes.data?.id;
      
      if (!id) {
        throw new Error('Onboarding data not found. Please complete your profile setup first.');
      }
      
      setOnboardingId(id);
      
      // Fetch verification status (documents)
      const docsRes = await axiosInstance.get(`/doctors/onboarding/${id}/verification_status/`);
      
      if (docsRes.data && typeof docsRes.data === 'object' && !docsRes.data.error) {
        // Handle both old format (just documents object) and new format (with requirements)
        const documentsData = docsRes.data.documents || docsRes.data;
        setDocuments(documentsData);
        
        // Update document types based on requirements if provided
        if (docsRes.data.requirements) {
          // Requirements will be used to mark which are required in the UI
          // This is informational only - all types can still be uploaded
        }
        
        setDocsError(null);
        setRetryCount(0);
      } else if (docsRes.data?.error) {
        throw new Error(docsRes.data.error);
      } else {
        // Empty documents - this is fine
        setDocuments({});
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.detail || 
                      err.message || 
                      'Failed to load documents';
      
      // If it's a network error and we have retries, retry
      if (retries > 0 && (!err.response || err.response.status >= 500)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchDocuments(retries - 1), 2000);
        return;
      }
      
      setDocsError(errorMsg);
      // Don't show error snackbar for 404 (no documents yet is fine)
      if (err.response?.status !== 404) {
        enqueueSnackbar(errorMsg, { variant: 'error' });
      }
    } finally {
      setLoadingDocs(false);
    }
  }, [enqueueSnackbar]);

  // Initial fetch
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Validate file before upload
  const validateFile = (selectedFile) => {
    setFileError(null);

    if (!selectedFile) {
      setFileError('Please select a file');
      return false;
    }

    // Check file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFileError(`File size must be less than ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(1)} MB`);
      return false;
    }

    // Check file extension
    const fileName = selectedFile.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      setFileError(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return false;
    }

    // Check MIME type if available
    if (selectedFile.type && !ALLOWED_TYPES.includes(selectedFile.type)) {
      setFileError(`Invalid file type. Allowed: PDF, JPG, PNG`);
      return false;
    }

    return true;
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      setFileError(null);
    } else if (selectedFile) {
      setFile(null);
      // Error already set by validateFile
    }
  };

  // Handle document upload with proper error handling
  const handleUpload = async () => {
    // Validation
    if (!docType) {
      enqueueSnackbar('Please select a document type', { variant: 'warning' });
      return;
    }

    if (!file) {
      enqueueSnackbar('Please select a file to upload', { variant: 'warning' });
      return;
    }

    if (!validateFile(file)) {
      return;
    }

    if (!onboardingId) {
      enqueueSnackbar('Onboarding ID not found. Please refresh the page.', { variant: 'error' });
      // Try to fetch onboarding ID
      try {
        const onboardingRes = await axiosInstance.get('/doctors/onboarding/');
        if (onboardingRes.data?.id) {
          setOnboardingId(onboardingRes.data.id);
        } else {
          return;
        }
      } catch (err) {
        enqueueSnackbar('Failed to get onboarding ID. Please try again.', { variant: 'error' });
        return;
      }
    }
    
    setUploadingDoc(true);
    setUploadProgress(0);
    setFileError(null);

    try {
      const formData = new FormData();
      formData.append('document_type', docType);
      formData.append('document', file);

      // Upload with progress tracking (if supported)
      const response = await axiosInstance.post(
        `/doctors/onboarding/${onboardingId}/upload_document/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            }
          },
          timeout: 60000, // 60 second timeout for large files
        }
      );

      enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
      
      // Reset form
      setFile(null);
      setDocType('');
      setFileError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadProgress(0);

      // Refresh document list
      await fetchDocuments();
      
    } catch (err) {
      console.error('Upload error:', err);
      
      let errorMsg = 'Failed to upload document';
      if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.message) {
        errorMsg = err.message;
      }

      // Handle specific error cases
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg = 'Upload timeout. Please try again with a smaller file.';
      } else if (err.response?.status === 413) {
        errorMsg = 'File too large. Maximum size is 5 MB.';
      } else if (err.response?.status === 400) {
        errorMsg = err.response.data?.error || err.response.data?.detail || 'Invalid file or document type';
      } else if (err.response?.status === 403) {
        errorMsg = 'You do not have permission to upload this document type.';
      } else if (!err.response) {
        errorMsg = 'Network error. Please check your connection and try again.';
      }

      setFileError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setUploadingDoc(false);
      setUploadProgress(0);
    }
  };

  // Handle document deletion/replacement
  const handleDeleteDocument = async (documentType) => {
    if (!window.confirm(`Are you sure you want to remove this ${documentType.replace('_', ' ')}?`)) {
      return;
    }

    try {
      // Note: Backend might not have delete endpoint, so we'll just allow re-upload
      // If document exists, user can re-upload to replace it
      enqueueSnackbar('You can upload a new document to replace the existing one.', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar('Failed to delete document', { variant: 'error' });
    }
  };

  // Handle document view
  const handleViewDocument = (document) => {
    if (document.url) {
      setSelectedDocument(document);
      setViewDialogOpen(true);
    } else {
      enqueueSnackbar('Document URL not available', { variant: 'warning' });
    }
  };

  // Handle document download
  const handleDownloadDocument = (doc) => {
    if (doc.url) {
      try {
        const link = document.createElement('a');
        link.href = doc.url;
        link.target = '_blank';
        link.download = doc.url.split('/').pop() || 'document';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        enqueueSnackbar('Download started', { variant: 'success' });
      } catch (err) {
        console.error('Download error:', err);
        // Fallback: open in new tab
        window.open(doc.url, '_blank', 'noopener,noreferrer');
        enqueueSnackbar('Opening document in new tab', { variant: 'info' });
      }
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'verified':
        return 'Verified';
      case 'pending':
        return 'Under Review';
      case 'rejected':
        return 'Rejected';
      default:
        return status || 'Unknown';
    }
  };

  // Loading state
  if (loadingDocs && !onboardingId) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading documents...
        </Typography>
      </Box>
    );
  }

  // Error state with retry
  if (docsError && !onboardingId && retryCount >= 3) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {docsError}
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            setRetryCount(0);
            fetchDocuments();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload Documents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload your verification documents. Required documents: Identity Proof, Qualification Certificate, and Registration Certificate.
        </Typography>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4}>
            <TextField
              label="Document Type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              select
              SelectProps={{ native: true }}
              fullWidth
              required
              disabled={uploadingDoc}
              error={!docType && file ? true : false}
              helperText={!docType && file ? 'Please select a document type' : ''}
            >
              <option value="">Select Document Type</option>
              {documentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} {type.required ? '*' : ''}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                disabled={uploadingDoc}
                style={{ display: 'none' }}
                id="document-upload-input"
              />
              <label htmlFor="document-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  disabled={uploadingDoc}
                  startIcon={<CloudUpload />}
                >
                  {file ? file.name : 'Choose File'}
                </Button>
              </label>
              {file && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Size: {formatFileSize(file.size)}
                  </Typography>
                </Box>
              )}
              {fileError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {fileError}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={uploadingDoc || !file || !docType}
              fullWidth
              startIcon={uploadingDoc ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {uploadingDoc ? `Uploading ${uploadProgress}%` : 'Upload'}
            </Button>
          </Grid>
        </Grid>

        {uploadingDoc && uploadProgress > 0 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </Paper>

      {/* Documents List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Uploaded Documents
        </Typography>

        {loadingDocs ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Loading documents...
            </Typography>
          </Box>
        ) : Object.keys(documents).length === 0 ? (
          <Box textAlign="center" py={4}>
            <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No documents uploaded yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Upload your verification documents to get started
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {Object.entries(documents).map(([type, doc]) => {
              const docTypeInfo = documentTypes.find(dt => dt.value === type);
              return (
                <Grid item xs={12} md={6} key={type}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {docTypeInfo?.label || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            {docTypeInfo?.required && (
                              <Chip
                                label="Required"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                        <Chip
                          label={getStatusLabel(doc.status)}
                          color={getStatusColor(doc.status)}
                          size="small"
                        />
                      </Box>

                      {doc.uploaded_at && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Uploaded: {new Date(doc.uploaded_at).toLocaleString()}
                        </Typography>
                      )}

                      {doc.rejection_reason && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Rejection Reason:</strong> {doc.rejection_reason}
                          </Typography>
                        </Alert>
                      )}

                      {doc.verified_at && doc.status === 'verified' && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Verified on: {new Date(doc.verified_at).toLocaleString()}
                          </Typography>
                        </Alert>
                      )}

                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        {doc.url && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => handleViewDocument(doc)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Download />}
                              onClick={() => handleDownloadDocument(doc)}
                            >
                              Download
                            </Button>
                          </>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<CloudUpload />}
                          onClick={() => {
                            setDocType(type);
                            fileInputRef.current?.click();
                          }}
                        >
                          Replace
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Document View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Document Preview</Typography>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDocument?.url ? (
            <Box>
              {selectedDocument.url.toLowerCase().includes('.pdf') || selectedDocument.url.includes('application/pdf') ? (
                <iframe
                  src={selectedDocument.url}
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                  title="Document Preview"
                  onError={() => {
                    enqueueSnackbar('Failed to load document preview. Try downloading instead.', { variant: 'warning' });
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <img
                    src={selectedDocument.url}
                    alt="Document Preview"
                    style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
                    onError={() => {
                      enqueueSnackbar('Failed to load document preview. Try downloading instead.', { variant: 'warning' });
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                Document URL not available
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedDocument?.url && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => handleDownloadDocument(selectedDocument)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PracticePaymentTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [practiceForm, setPracticeForm] = useState({});
  const [loadingPractice, setLoadingPractice] = useState(true);
  const [savingPractice, setSavingPractice] = useState(false);
  const [practiceError, setPracticeError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [practiceEditMode, setPracticeEditMode] = useState(false);
  const [originalForm, setOriginalForm] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Fetch practice/payment data with retry logic
  const fetchPractice = React.useCallback(async (retries = 3) => {
    setLoadingPractice(true);
    setPracticeError(null);
    try {
      const res = await axiosInstance.get('/doctors/doctors/me/');
      setPracticeForm(res.data);
      setOriginalForm(res.data);
      setRetryCount(0);
      setFormErrors({});
    } catch (err) {
      console.error('Error fetching practice/payment info:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.error || 
                          err.message || 
                          'Failed to load practice/payment info';
      
      // Retry on network/server errors
      if (retries > 0 && (!err.response || err.response.status >= 500)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchPractice(retries - 1), 2000);
        return;
      }
      
      setPracticeError(errorMessage);
      if (err.response?.status !== 404) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setLoadingPractice(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPractice();
  }, [fetchPractice]);

  // Validation functions
  const validateIFSC = (ifsc) => {
    if (!ifsc) return null;
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(ifsc.toUpperCase())) {
      return 'IFSC code must be 11 characters (e.g., ABCD0123456)';
    }
    return null;
  };

  const validatePAN = (pan) => {
    if (!pan) return null;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(pan.toUpperCase())) {
      return 'PAN must be 10 characters (e.g., ABCDE1234F)';
    }
    return null;
  };

  const validateGST = (gst) => {
    if (!gst) return null;
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstRegex.test(gst.toUpperCase())) {
      return 'GST number must be 15 characters';
    }
    return null;
  };

  const validateWebsite = (website) => {
    if (!website) return null;
    try {
      const url = new URL(website.startsWith('http') ? website : `https://${website}`);
      if (!['http:', 'https:'].includes(url.protocol)) {
        return 'Website must start with http:// or https://';
      }
    } catch {
      return 'Please enter a valid website URL';
    }
    return null;
  };

  const validateAccountNumber = (account) => {
    if (!account) return null;
    if (account.length < 9 || account.length > 18) {
      return 'Account number must be between 9 and 18 digits';
    }
    if (!/^\d+$/.test(account)) {
      return 'Account number must contain only digits';
    }
    return null;
  };

  const validateField = (field, value) => {
    let error = null;
    switch (field) {
      case 'ifsc_code':
        error = validateIFSC(value);
        break;
      case 'pan_number':
        error = validatePAN(value);
        break;
      case 'gst_number':
        error = validateGST(value);
        break;
      case 'website':
        error = validateWebsite(value);
        break;
      case 'account_number':
        error = validateAccountNumber(value);
        break;
      case 'consultation_fee':
      case 'follow_up_fee':
      case 'minimum_payout':
        if (value !== undefined && value !== null && value !== '') {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0) {
            error = 'Must be a positive number';
          } else if (numValue > 1000000) {
            error = 'Value too large';
          }
        }
        break;
      default:
        break;
    }
    setFormErrors(prev => ({ ...prev, [field]: error }));
    return error === null;
  };

  const handleChange = (field, value) => {
    setPracticeForm((prev) => {
      const updated = { ...prev, [field]: value };
      validateField(field, value);
      return updated;
    });
  };

  const handleCancel = () => {
    setPracticeForm(originalForm);
    setFormErrors({});
    setPracticeEditMode(false);
  };

  const validateForm = () => {
    let isValid = true;
    const fieldsToValidate = ['ifsc_code', 'pan_number', 'gst_number', 'website', 'account_number', 
                              'consultation_fee', 'follow_up_fee', 'minimum_payout'];
    
    fieldsToValidate.forEach(field => {
      if (practiceForm[field] !== undefined && practiceForm[field] !== null && practiceForm[field] !== '') {
        if (!validateField(field, practiceForm[field])) {
          isValid = false;
        }
      }
    });

    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors before saving', { variant: 'error' });
      return;
    }

    setSavingPractice(true);
    setPracticeError(null);

    try {
      const submitData = { ...practiceForm };
      
      // Ensure consultation_types and availability_days are arrays
      if (submitData.consultation_types && typeof submitData.consultation_types === 'string') {
        submitData.consultation_types = submitData.consultation_types.split(',').map(t => t.trim()).filter(Boolean);
      } else if (Array.isArray(submitData.consultation_types)) {
        submitData.consultation_types = submitData.consultation_types.filter(Boolean);
      }
      
      if (submitData.availability_days && typeof submitData.availability_days === 'string') {
        submitData.availability_days = submitData.availability_days.split(',').map(d => d.trim()).filter(Boolean);
      } else if (Array.isArray(submitData.availability_days)) {
        submitData.availability_days = submitData.availability_days.filter(Boolean);
      }
      
      // Normalize website URL
      if (submitData.website && !submitData.website.startsWith('http')) {
        submitData.website = `https://${submitData.website}`;
      }
      
      // Normalize IFSC, PAN, GST to uppercase
      if (submitData.ifsc_code) submitData.ifsc_code = submitData.ifsc_code.toUpperCase();
      if (submitData.pan_number) submitData.pan_number = submitData.pan_number.toUpperCase();
      if (submitData.gst_number) submitData.gst_number = submitData.gst_number.toUpperCase();
      
      // Remove nested objects that shouldn't be sent
      delete submitData.user;
      delete submitData.clinic;
      delete submitData.ratings;
      delete submitData.verification;
      delete submitData.average_rating;
      delete submitData.qualifications;
      delete submitData.specialities;
      delete submitData.languages;
      delete submitData.id;
      
      const response = await axiosInstance.put('/doctors/doctors/me/', submitData, {
        timeout: 30000,
      });
      
      enqueueSnackbar('Practice & Payment info updated successfully', { variant: 'success' });
      setPracticeForm(response.data || submitData);
      setOriginalForm(response.data || submitData);
      setPracticeEditMode(false);
      setPracticeError(null);
      setFormErrors({});
    } catch (err) {
      console.error('Save error:', err);
      
      let errorMessage = 'Failed to update practice/payment info';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (typeof err.response.data === 'object') {
          const fieldErrors = Object.entries(err.response.data).map(([field, errors]) => {
            return `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`;
          }).join('; ');
          errorMessage = fieldErrors || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setPracticeError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingPractice(false);
    }
  };

  if (loadingPractice) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading practice & payment info...
        </Typography>
      </Box>
    );
  }

  if (practiceError && !practiceForm && Object.keys(practiceForm).length === 0 && retryCount >= 3) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {practiceError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setRetryCount(0);
            fetchPractice();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>Practice & Payment Setup</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure your practice details and payment information for receiving payments.
          </Typography>
        </Box>

        {practiceError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPracticeError(null)}>
            {practiceError}
          </Alert>
        )}

        <Box component="form" noValidate autoComplete="off">
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
            Practice Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Consultation Fee"
                type="number"
                value={practiceForm.consultation_fee || ''}
                onChange={e => handleChange('consultation_fee', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.consultation_fee}
                helperText={formErrors.consultation_fee || 'Fee in Indian Rupees'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, max: 1000000, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Follow-up Fee"
                type="number"
                value={practiceForm.follow_up_fee || ''}
                onChange={e => handleChange('follow_up_fee', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.follow_up_fee}
                helperText={formErrors.follow_up_fee || 'Follow-up consultation fee'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, max: 1000000, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Consultation Types"
                value={practiceForm.consultation_types ? (Array.isArray(practiceForm.consultation_types) ? practiceForm.consultation_types.join(', ') : practiceForm.consultation_types) : ''}
                onChange={e => handleChange('consultation_types', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                helperText="Comma separated (e.g., In-person, Video, Phone)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Availability Days"
                value={practiceForm.availability_days ? (Array.isArray(practiceForm.availability_days) ? practiceForm.availability_days.join(', ') : practiceForm.availability_days) : ''}
                onChange={e => handleChange('availability_days', e.target.value.split(',').map(d => d.trim()).filter(Boolean))}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                helperText="Comma separated (e.g., Monday, Tuesday, Wednesday)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Professional Summary"
                value={practiceForm.professional_summary || ''}
                onChange={e => handleChange('professional_summary', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                multiline
                rows={4}
                helperText="Brief description of your practice and expertise"
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Website"
                value={practiceForm.website || ''}
                onChange={e => handleChange('website', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.website}
                helperText={formErrors.website || 'Your professional website URL'}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />
          
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
            Payment Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bank Name"
                value={practiceForm.bank_name || ''}
                onChange={e => handleChange('bank_name', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                helperText="Name of your bank"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account Holder Name"
                value={practiceForm.account_holder_name || ''}
                onChange={e => handleChange('account_holder_name', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                helperText="Name as on bank account"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account Number"
                value={practiceForm.account_number || ''}
                onChange={e => handleChange('account_number', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.account_number}
                helperText={formErrors.account_number || 'Bank account number'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="IFSC Code"
                value={practiceForm.ifsc_code || ''}
                onChange={e => handleChange('ifsc_code', e.target.value.toUpperCase())}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.ifsc_code}
                helperText={formErrors.ifsc_code || '11 character IFSC code (e.g., ABCD0123456)'}
                inputProps={{ maxLength: 11, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PAN Number"
                value={practiceForm.pan_number || ''}
                onChange={e => handleChange('pan_number', e.target.value.toUpperCase())}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.pan_number}
                helperText={formErrors.pan_number || '10 character PAN (e.g., ABCDE1234F)'}
                inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                value={practiceForm.gst_number || ''}
                onChange={e => handleChange('gst_number', e.target.value.toUpperCase())}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.gst_number}
                helperText={formErrors.gst_number || '15 character GST number'}
                inputProps={{ maxLength: 15, style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Cycle"
                value={practiceForm.payment_cycle || ''}
                onChange={e => handleChange('payment_cycle', e.target.value)}
                fullWidth
                select
                SelectProps={{ native: true }}
                disabled={!practiceEditMode || savingPractice}
              >
                <option value="">Select Payment Cycle</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Payout"
                type="number"
                value={practiceForm.minimum_payout || ''}
                onChange={e => handleChange('minimum_payout', e.target.value)}
                fullWidth
                disabled={!practiceEditMode || savingPractice}
                error={!!formErrors.minimum_payout}
                helperText={formErrors.minimum_payout || 'Minimum amount for payout'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                inputProps={{ min: 0, max: 1000000 }}
              />
            </Grid>
          </Grid>
          
          <Box mt={3} sx={{ display: 'flex', gap: 2 }}>
            {practiceEditMode ? (
              <>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSave}
                  disabled={savingPractice}
                  startIcon={savingPractice ? <CircularProgress size={20} /> : <Save />}
                >
                  {savingPractice ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleCancel}
                  disabled={savingPractice}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                variant="contained" 
                onClick={() => setPracticeEditMode(true)}
                startIcon={<Edit />}
              >
                Edit Practice & Payment Info
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const NotificationsTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [preferenceMatrix, setPreferenceMatrix] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [originalPreferences, setOriginalPreferences] = useState({});
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [notificationsEditMode, setNotificationsEditMode] = useState(false);

  // Common notification events and channels
  const notificationEvents = [
    { key: 'appointment_created', label: 'Appointment Created', transactional: true },
    { key: 'appointment_reminder', label: 'Appointment Reminder', transactional: true },
    { key: 'appointment_cancelled', label: 'Appointment Cancelled', transactional: true },
    { key: 'appointment_rescheduled', label: 'Appointment Rescheduled', transactional: true },
    { key: 'payment_success', label: 'Payment Success', transactional: true },
    { key: 'payment_failed', label: 'Payment Failed', transactional: true },
    { key: 'marketing', label: 'Marketing & Promotions', transactional: false },
    { key: 'account_verification', label: 'Account Verification', transactional: true },
  ];

  const notificationChannels = [
    { key: 'email', label: 'Email', icon: Email },
    { key: 'sms', label: 'SMS', icon: Phone },
    { key: 'whatsapp', label: 'WhatsApp', icon: Chat },
    { key: 'in_app', label: 'In-App', icon: Notifications },
  ];

  // Fetch notification preferences with retry logic
  const fetchPreferences = React.useCallback(async (retries = 3) => {
    setLoadingNotifications(true);
    setNotificationsError(null);
    try {
      const response = await axiosInstance.get('/notifications/api/preferences/matrix/');
      
      if (response.data?.preferences) {
        setPreferenceMatrix(response.data);
        setPreferences(response.data.preferences || {});
        setOriginalPreferences(JSON.parse(JSON.stringify(response.data.preferences || {})));
        setRetryCount(0);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.detail || 
                          err.message || 
                          'Failed to load notification preferences';
      
      // Retry on network/server errors
      if (retries > 0 && (!err.response || err.response.status >= 500)) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchPreferences(retries - 1), 2000);
        return;
      }
      
      setNotificationsError(errorMessage);
      if (err.response?.status !== 404) {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setLoadingNotifications(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Handle preference change
  const handlePreferenceChange = (eventKey, channel, value) => {
    setPreferences(prev => {
      const updated = { ...prev };
      if (!updated[eventKey]) {
        updated[eventKey] = {};
      }
      updated[eventKey][channel] = value;
      return updated;
    });
  };

  const handleCancel = () => {
    setPreferences(JSON.parse(JSON.stringify(originalPreferences)));
    setNotificationsEditMode(false);
    setNotificationsError(null);
  };

  const handleSave = async () => {
    setSavingNotifications(true);
    setNotificationsError(null);
    
    try {
      // Build updates array from preferences state
      const updates = [];
      
      // Iterate through all events and channels
      notificationEvents.forEach(event => {
        notificationChannels.forEach(channel => {
          const currentValue = preferences[event.key]?.[channel.key];
          const originalValue = originalPreferences[event.key]?.[channel.key];
          
          // Only send update if value changed
          if (currentValue !== undefined && currentValue !== originalValue) {
            updates.push({
              event_key: event.key,
              channel: channel.key,
              opt_in: currentValue === true,
            });
          }
        });
      });
      
      if (updates.length === 0) {
        enqueueSnackbar('No changes to save', { variant: 'info' });
        setNotificationsEditMode(false);
        return;
      }
      
      // Bulk update preferences
      const response = await axiosInstance.patch('/notifications/api/preferences/bulk_update/', {
        updates: updates,
      }, {
        timeout: 30000,
      });
      
      const successMessage = response.data?.message || 'Notification preferences updated successfully';
      enqueueSnackbar(successMessage, { variant: 'success' });
      setNotificationsEditMode(false);
      
      // Refresh preferences
      await fetchPreferences();
    } catch (err) {
      console.error('Save error:', err);
      
      let errorMessage = 'Failed to update notification preferences';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.errors) {
          // Handle validation errors
          const fieldErrors = Object.entries(err.response.data.errors).map(([field, errors]) => {
            return `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`;
          }).join('; ');
          errorMessage = fieldErrors || errorMessage;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setNotificationsError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingNotifications(false);
    }
  };

  // Get preference value with fallback to default
  const getPreferenceValue = (eventKey, channel) => {
    if (preferences[eventKey]?.[channel] !== undefined) {
      return preferences[eventKey][channel] === true;
    }
    // Default: transactional events are enabled, marketing is disabled
    const event = notificationEvents.find(e => e.key === eventKey);
    return event?.transactional === true;
  };

  if (loadingNotifications) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading notification preferences...
        </Typography>
      </Box>
    );
  }

  if (notificationsError && !preferenceMatrix && retryCount >= 3) {
    return (
      <Box textAlign="center" mt={4} mb={4}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {notificationsError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setRetryCount(0);
            fetchPreferences();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" gutterBottom>Notification Preferences</Typography>
            <Typography variant="body2" color="text.secondary">
              Control how and when you receive notifications. Transactional notifications (appointments, payments) are enabled by default.
            </Typography>
          </Box>
          {!notificationsEditMode && (
            <Button 
              variant="contained" 
              startIcon={<Edit />} 
              onClick={() => setNotificationsEditMode(true)}
            >
              Edit Preferences
            </Button>
          )}
        </Box>
        
        {notificationsError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setNotificationsError(null)}>
            {notificationsError}
          </Alert>
        )}

        {preferenceMatrix?.summary && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Summary:</strong> {preferenceMatrix.summary.enabled_count || 0} enabled, {' '}
              {preferenceMatrix.summary.disabled_count || 0} disabled notifications
            </Typography>
          </Alert>
        )}

        <Box>
          {/* Transactional Notifications */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
            Transactional Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Important notifications about appointments, payments, and account activity
          </Typography>
          
          {notificationEvents
            .filter(event => event.transactional)
            .map(event => (
              <Box key={event.key} sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                  {event.label}
                </Typography>
                <Grid container spacing={2}>
                  {notificationChannels.map(channel => {
                    const ChannelIcon = channel.icon;
                    const isEnabled = getPreferenceValue(event.key, channel.key);
                    return (
                      <Grid item xs={12} sm={6} md={3} key={channel.key}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={isEnabled}
                              onChange={e => handlePreferenceChange(event.key, channel.key, e.target.checked)}
                              disabled={!notificationsEditMode || savingNotifications}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ChannelIcon fontSize="small" />
                              <Typography variant="body2">{channel.label}</Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))}

          {/* Marketing & Promotional */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
            Marketing & Promotional
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Promotional emails and marketing communications
          </Typography>
          
          {notificationEvents
            .filter(event => !event.transactional)
            .map(event => (
              <Box key={event.key} sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                  {event.label}
                </Typography>
                <Grid container spacing={2}>
                  {notificationChannels.map(channel => {
                    const ChannelIcon = channel.icon;
                    const isEnabled = getPreferenceValue(event.key, channel.key);
                    return (
                      <Grid item xs={12} sm={6} md={3} key={channel.key}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={isEnabled}
                              onChange={e => handlePreferenceChange(event.key, channel.key, e.target.checked)}
                              disabled={!notificationsEditMode || savingNotifications}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ChannelIcon fontSize="small" />
                              <Typography variant="body2">{channel.label}</Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            ))}
        </Box>
        
        {notificationsEditMode && (
          <Box mt={4} sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSave}
              disabled={savingNotifications}
              startIcon={savingNotifications ? <CircularProgress size={20} /> : <Save />}
            >
              {savingNotifications ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleCancel}
              disabled={savingNotifications}
            >
              Cancel
            </Button>
            <Button
              variant="text"
              color="secondary"
              onClick={async () => {
                if (window.confirm('Reset all notification preferences to defaults? This cannot be undone.')) {
                  try {
                    setSavingNotifications(true);
                    await axiosInstance.post('/notifications/api/preferences/reset_defaults/');
                    enqueueSnackbar('Preferences reset to defaults', { variant: 'success' });
                    await fetchPreferences();
                    setNotificationsEditMode(false);
                  } catch (err) {
                    const errorMessage = err.response?.data?.error || err.response?.data?.detail || 'Failed to reset preferences';
                    enqueueSnackbar(errorMessage, { variant: 'error' });
                  } finally {
                    setSavingNotifications(false);
                  }
                }
              }}
              disabled={savingNotifications}
            >
              Reset to Defaults
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const SecurityTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [securityError, setSecurityError] = useState(null);
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    label: 'Very Weak'
  });
  const [formErrors, setFormErrors] = useState({});

  // Password strength validation
  const calculatePasswordStrength = (password) => {
    if (!password) {
      return { score: 0, feedback: [], label: 'Very Weak' };
    }

    let score = 0;
    const feedback = [];

    if (password.length >= 8) score += 1;
    else feedback.push('At least 8 characters');
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Lowercase letters');
    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Uppercase letters');
    if (/[0-9]/.test(password)) score += 1;
    else feedback.push('Numbers');
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    else feedback.push('Special characters');

    const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    return {
      score: Math.min(score, 5),
      feedback: score >= 4 ? [] : feedback,
      label: strengthLabels[Math.floor((score / 5) * strengthLabels.length)] || 'Very Weak'
    };
  };

  const validatePassword = (password) => {
    if (!password) return null;
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (password.length > 128) return 'Password must be less than 128 characters';
    const strength = calculatePasswordStrength(password);
    if (strength.score < 3) {
      return 'Password is too weak. Use a mix of letters, numbers, and special characters.';
    }
    if (password === form.currentPassword) {
      return 'New password must be different from current password';
    }
    return null;
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'newPassword') {
        const strength = calculatePasswordStrength(value);
        setPasswordStrength(strength);
        const error = validatePassword(value);
        setFormErrors(prev => ({ ...prev, newPassword: error }));
      }
      if (field === 'confirmPassword' || field === 'newPassword') {
        if (updated.confirmPassword && updated.newPassword !== updated.confirmPassword) {
          setFormErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
        } else {
          setFormErrors(prev => ({ ...prev, confirmPassword: null }));
        }
      }
      return updated;
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;
    if (!form.currentPassword) {
      errors.currentPassword = 'Current password is required';
      isValid = false;
    }
    if (!form.newPassword) {
      errors.newPassword = 'New password is required';
      isValid = false;
    } else {
      const error = validatePassword(form.newPassword);
      if (error) {
        errors.newPassword = error;
        isValid = false;
      }
    }
    if (!form.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
      isValid = false;
    } else if (form.newPassword !== form.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };

  const handlePasswordChange = async () => {
    setSecurityError(null);
    if (!validateForm()) {
      enqueueSnackbar('Please fix validation errors before saving', { variant: 'error' });
      return;
    }
    
    setLoadingSecurity(true);
    
    try {
      const response = await axiosInstance.post('/auth/password/change/', {
        old_password: form.currentPassword,
        new_password: form.newPassword,
        confirm_password: form.confirmPassword,
      }, {
        timeout: 30000,
      });
      
      enqueueSnackbar(response.data?.message || 'Password changed successfully', { variant: 'success' });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSecurityError(null);
      setFormErrors({});
      setPasswordStrength({ score: 0, feedback: [], label: 'Very Weak' });
      setShowPassword({ current: false, new: false, confirm: false });
    } catch (err) {
      console.error('Password change error:', err);
      let errorMessage = 'Failed to change password';
      if (err.response?.data) {
        if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (err.response?.status === 400) {
        if (errorMessage.toLowerCase().includes('current') || errorMessage.toLowerCase().includes('old')) {
          setFormErrors(prev => ({ ...prev, currentPassword: 'Current password is incorrect' }));
        }
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timeout. Please check your connection and try again.';
      } else if (!err.response) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setSecurityError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoadingSecurity(false);
    }
  };

  const getStrengthColor = (score) => {
    if (score <= 1) return 'error';
    if (score <= 2) return 'warning';
    if (score <= 3) return 'info';
    return 'success';
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" mb={2}>Security Settings</Typography>
      <Box component="form" noValidate autoComplete="off">
        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="Current Password"
          value={form.currentPassword}
          onChange={e => handleChange('currentPassword', e.target.value)}
          margin="normal"
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />
        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="New Password"
          value={form.newPassword}
          onChange={e => handleChange('newPassword', e.target.value)}
          margin="normal"
          helperText="Must be at least 8 characters long"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="Confirm New Password"
          value={form.confirmPassword}
          onChange={e => handleChange('confirmPassword', e.target.value)}
          margin="normal"
          error={form.confirmPassword && form.newPassword !== form.confirmPassword}
          helperText={form.confirmPassword && form.newPassword !== form.confirmPassword ? 'Passwords do not match' : ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {securityError && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {securityError}
          </Alert>
        )}
        <Box mt={2} sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handlePasswordChange} 
            disabled={loadingSecurity || !form.currentPassword || !form.newPassword || !form.confirmPassword}
            startIcon={loadingSecurity ? <CircularProgress size={20} /> : <Lock />}
          >
            {loadingSecurity ? 'Changing Password...' : 'Change Password'}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => {
              setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setSecurityError(null);
            }}
            disabled={loadingSecurity}
          >
            Clear
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const Settings = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: profileData, loading: profileLoading } = useSelector((state) => state.profile);
  const { data: clinicData, loading: clinicLoading } = useSelector((state) => state.clinic);

  // Use our new subscription context
  const {
    subscriptionStatus,
    hasActiveSubscription,
    isOnTrial,
    getPlanType,
    getDaysRemaining,
    getCurrentUsage,
    getRemainingQuota,
    getUsagePercentage,
    isUsageLimitReached,
    isUsageLimitNear,
    getUpgradeRecommendations,
    refresh: refreshSubscription,
    loading: subscriptionLoading,
    error: subscriptionError
  } = useSubscriptionContext();

  // Get authentication state from Redux instead of subscription context
  const { user, accessToken } = useSelector((state) => state.auth);
  const isAuthenticated = !!user && !!accessToken;

  const [activeTab, setActiveTab] = useState(0);

  // Handle navigation state to set active tab
  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
    }
    // Also handle URL parameters for direct navigation
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'subscription') {
      setActiveTab(5);
    }
  }, [location.state, location.search]);
  const [editMode, setEditMode] = useState(false);
  const [subscriptionDialog, setSubscriptionDialog] = useState(false);
  const [paymentMethodDialog, setPaymentMethodDialog] = useState(false);
  const [planSummaryDialog, setPlanSummaryDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Legacy state for backward compatibility
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [trialStatus, setTrialStatus] = useState(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  
  // New state for subscription UI interactions
  const [subscriptionView, setSubscriptionView] = useState('plans'); // 'plans', 'billing', 'faq'
  const [selectedPlanForAction, setSelectedPlanForAction] = useState(null);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    profile: {
      fullName: '',
      email: '',
      phone: '',
      gender: '',
      dateOfBirth: '',
      address: '',
    },
    professionalDetails: {
      specializations: [],
      qualifications: [],
      experience: '',
      languages: [],
      bio: '',
    },
    documents: {
      medicalLicense: null,
      identityProof: null,
      certifications: [],
    },
    practiceSetup: {
      clinicName: '',
      clinicAddress: '',
      consultationFee: '',
      followUpFee: '',
      consultationTypes: [],
      availabilityDays: [],
      services: [],
      professionalSummary: '',
      website: '',
    },
    paymentSetup: {
      bankName: '',
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      panNumber: '',
      gstNumber: '',
      paymentCycle: '',
      minimumPayout: '',
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: true,
      appointmentReminders: true,
      marketingEmails: false,
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      twoFactorAuth: false,
    },
  });
  
  // Other necessary state
  const [showPassword, setShowPassword] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Add missing hooks and state
  const { Razorpay } = useRazorpay();
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(null);

  // Move fetchCurrentSubscription outside useEffect so it can be called anywhere
  const fetchCurrentSubscription = async () => {
    try {
      const response = await axiosInstance.get('/payments/subscriptions/');
      setSubscription(response.data[0]); // Assuming first is active
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axiosInstance.get('/payments/plans/');
        setPlans(response.data);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
    fetchCurrentSubscription();
  }, []);
  


  useEffect(() => {
    if (profileData) {
      setFormData(prev => ({
        ...prev,
        profile: {
          fullName: profileData.full_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          gender: profileData.gender || '',
          dateOfBirth: profileData.date_of_birth || '',
          address: profileData.address || '',
        },
      }));
    }
  }, [profileData]);

  useEffect(() => {
    if (clinicData) {
      setFormData(prev => ({
        ...prev,
        clinic: {
          name: clinicData.clinic_name || '',
          address: clinicData.address || '',
          phone: clinicData.phone_number || '',
          email: clinicData.email || '',
          workingHours: clinicData.working_hours || '',
          services: clinicData.services_offered?.split(',').map(s => s.trim()) || [],
        },
      }));
    }
  }, [clinicData]);

  useEffect(() => {
    const fetchTrialStatus = async () => {
      try {
        const response = await axiosInstance.get('/payments/trial/');
        setTrialStatus(response.data);
      } catch (error) {
        setTrialStatus(null);
      }
    };
    fetchTrialStatus();
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSave = async (section) => {
    try {
      if (section === 'profile') {
        await dispatch(updateDoctorProfile(formData.profile)).unwrap();
      } else if (section === 'clinic') {
        await dispatch(updateClinicProfile(formData.clinic)).unwrap();
      }
      setEditMode(false);
    } catch (error) {
      console.error('Error saving changes:', error);
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleInputChange('profile', 'avatar', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpgradePlan = async () => {
    try {
      const response = await axiosInstance.post('/payments/subscribe/', {
        plan_id: selectedPlan.id,
      });
      setSubscription(response.data);
      setSubscriptionDialog(false);
    } catch (error) {
      console.error('Subscription upgrade failed:', error);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await axiosInstance.post('/payments/subscription/cancel/');
      enqueueSnackbar('Subscription cancelled successfully', { variant: 'success' });
      fetchCurrentSubscription();
    } catch (error) {
      enqueueSnackbar('Failed to cancel subscription', { variant: 'error' });
    }
  };

  const handleUpgradeSubscription = async (newPlanId) => {
    try {
      await axiosInstance.post('/payments/subscription/upgrade/', { plan_id: newPlanId });
      enqueueSnackbar('Subscription upgraded successfully', { variant: 'success' });
      fetchCurrentSubscription();
    } catch (error) {
      enqueueSnackbar('Failed to upgrade subscription', { variant: 'error' });
    }
  };

  const handleDowngradeSubscription = async (newPlanId) => {
    try {
      await axiosInstance.post('/payments/subscription/downgrade/', { plan_id: newPlanId });
      enqueueSnackbar('Subscription downgraded successfully', { variant: 'success' });
      fetchCurrentSubscription();
    } catch (error) {
      enqueueSnackbar('Failed to downgrade subscription', { variant: 'error' });
    }
  };

  const handleStartTrial = async () => {
    try {
      await axiosInstance.post('/payments/subscription/start_trial/');
      enqueueSnackbar('Trial started successfully', { variant: 'success' });
      // Refresh trial status
      const response = await axiosInstance.get('/payments/trial/');
      setTrialStatus(response.data);
    } catch (error) {
      enqueueSnackbar('Failed to start trial', { variant: 'error' });
    }
  };

  const handlePauseSubscription = async () => {
    try {
      await axiosInstance.post('/payments/subscription/pause/');
      enqueueSnackbar('Subscription paused successfully', { variant: 'success' });
      fetchCurrentSubscription();
    } catch (error) {
      enqueueSnackbar('Failed to pause subscription', { variant: 'error' });
    }
  };

  const handleResumeSubscription = async () => {
    try {
      await axiosInstance.post('/payments/subscription/resume/');
      enqueueSnackbar('Subscription resumed successfully', { variant: 'success' });
      fetchCurrentSubscription();
    } catch (error) {
      enqueueSnackbar('Failed to resume subscription', { variant: 'error' });
    }
  };

  // Utility functions for enhanced UX
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPlanColor = (planType) => {
    switch (planType) {
      case 'basic':
        return theme.palette.primary.main;
      case 'professional':
        return theme.palette.secondary.main;
      case 'enterprise':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'basic':
        return <StarBorder />;
      case 'professional':
        return <Star />;
      case 'enterprise':
        return <TrendingUp />;
      default:
        return <Store />;
    }
  };

  const calculateSavings = (monthlyPrice, annualPrice) => {
    if (!annualPrice) return 0;
    const monthlyTotal = monthlyPrice * 12;
    const savings = ((monthlyTotal - annualPrice) / monthlyTotal) * 100;
    return Math.round(savings);
  };
  
  

  const calculateProfileCompletion = () => {
    const sections = [
      { name: 'Basic Information', weight: 0.2 },
      { name: 'Professional Details', weight: 0.25 },
      { name: 'Document Verification', weight: 0.2 },
      { name: 'Practice Setup', weight: 0.2 },
      { name: 'Payment Setup', weight: 0.15 },
    ];

    let totalCompletion = 0;
    sections.forEach(section => {
      const sectionData = formData[section.name.toLowerCase().replace(/\s+/g, '')];
      if (sectionData) {
        const fields = Object.keys(sectionData);
        const completedFields = fields.filter(field => sectionData[field] !== null && sectionData[field] !== '');
        totalCompletion += (completedFields.length / fields.length) * section.weight;
      }
    });

    return Math.round(totalCompletion * 100);
  };

  const renderProfileCompletion = () => (
    <ProfileCompletionCard>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Profile Completion
        </Typography>
        <Typography variant="h4" color="primary">
          {calculateProfileCompletion()}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={calculateProfileCompletion()} 
        sx={{ 
          height: 8, 
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          },
        }} 
      />
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Complete your profile to improve your visibility and trust among patients
        </Typography>
      </Box>
    </ProfileCompletionCard>
  );

  const renderBasicInformation = () => (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Basic Information</Typography>
          <Button
            startIcon={editMode ? <Save /> : <Edit />}
            onClick={() => editMode ? handleSave('profile') : setEditMode(true)}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.profile.fullName}
              onChange={(e) => handleInputChange('profile', 'fullName', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              value={formData.profile.email}
              onChange={(e) => handleInputChange('profile', 'email', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.profile.phone}
              onChange={(e) => handleInputChange('profile', 'phone', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Gender"
              value={formData.profile.gender}
              onChange={(e) => handleInputChange('profile', 'gender', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={formData.profile.dateOfBirth}
              onChange={(e) => handleInputChange('profile', 'dateOfBirth', e.target.value)}
              disabled={!editMode}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={formData.profile.address}
              onChange={(e) => handleInputChange('profile', 'address', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  const renderProfessionalDetails = () => (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Professional Details</Typography>
          <Button
            startIcon={editMode ? <Save /> : <Edit />}
            onClick={() => editMode ? handleSave('professionalDetails') : setEditMode(true)}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Specializations"
              value={formData.professionalDetails.specializations.join(', ')}
              onChange={(e) => handleInputChange('professionalDetails', 'specializations', e.target.value.split(',').map(s => s.trim()))}
              disabled={!editMode}
              helperText="Separate multiple specializations with commas"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Qualifications"
              value={formData.professionalDetails.qualifications.join(', ')}
              onChange={(e) => handleInputChange('professionalDetails', 'qualifications', e.target.value.split(',').map(q => q.trim()))}
              disabled={!editMode}
              helperText="Separate multiple qualifications with commas"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Experience (years)"
              type="number"
              value={formData.professionalDetails.experience}
              onChange={(e) => handleInputChange('professionalDetails', 'experience', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Languages"
              value={formData.professionalDetails.languages.join(', ')}
              onChange={(e) => handleInputChange('professionalDetails', 'languages', e.target.value.split(',').map(l => l.trim()))}
              disabled={!editMode}
              helperText="Separate multiple languages with commas"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Professional Bio"
              multiline
              rows={4}
              value={formData.professionalDetails.bio}
              onChange={(e) => handleInputChange('professionalDetails', 'bio', e.target.value)}
              disabled={!editMode}
              helperText="Write a brief description about your professional background and expertise"
            />
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  const handleFileUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image (JPEG/PNG) or PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('field', field);

      // Here you would typically make an API call to upload the file
      // For now, we'll just update the local state
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [field]: {
            file: file,
            name: file.name,
            type: file.type,
            size: file.size
          }
        }
      }));
    } catch (error) {
      setError('Failed to upload file. Please try again.');
    }
  };

  const handleRemoveFile = (field) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [field]: null
      }
    }));
  };

  const handleRemoveCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        certifications: prev.documents.certifications.filter((_, i) => i !== index)
      }
    }));
  };

  const renderDocumentVerification = () => (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Document Verification</Typography>
          <Button
            startIcon={<CloudUpload />}
            onClick={() => document.getElementById('document-upload').click()}
          >
            Upload Documents
          </Button>
        </Box>
        <input
          id="document-upload"
          type="file"
          multiple
          hidden
          onChange={handleFileUpload}
          accept=".pdf,.jpg,.jpeg,.png"
        />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Medical License</Typography>
            {formData.documents.medicalLicense ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Typography variant="body2">{formData.documents.medicalLicense.name}</Typography>
                <IconButton size="small" onClick={() => handleRemoveFile('medicalLicense')}>
                  <Delete />
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No file uploaded</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Identity Proof</Typography>
            {formData.documents.identityProof ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Typography variant="body2">{formData.documents.identityProof.name}</Typography>
                <IconButton size="small" onClick={() => handleRemoveFile('identityProof')}>
                  <Delete />
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No file uploaded</Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Additional Certifications</Typography>
            <List>
              {formData.documents.certifications.map((cert, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary={cert.name} />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleRemoveCertification(index)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  const renderPracticeSetup = () => (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Practice Setup</Typography>
          <Button
            startIcon={editMode ? <Save /> : <Edit />}
            onClick={() => editMode ? handleSave('practiceSetup') : setEditMode(true)}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Clinic Name"
              value={formData.practiceSetup.clinicName}
              onChange={(e) => handleInputChange('practiceSetup', 'clinicName', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Clinic Address"
              multiline
              rows={2}
              value={formData.practiceSetup.clinicAddress}
              onChange={(e) => handleInputChange('practiceSetup', 'clinicAddress', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Consultation Fee"
              type="number"
              value={formData.practiceSetup.consultationFee}
              onChange={(e) => handleInputChange('practiceSetup', 'consultationFee', e.target.value)}
              disabled={!editMode}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Follow-up Fee"
              type="number"
              value={formData.practiceSetup.followUpFee}
              onChange={(e) => handleInputChange('practiceSetup', 'followUpFee', e.target.value)}
              disabled={!editMode}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Consultation Types"
              value={formData.practiceSetup.consultationTypes.join(', ')}
              onChange={(e) => handleInputChange('practiceSetup', 'consultationTypes', e.target.value.split(',').map(t => t.trim()))}
              disabled={!editMode}
              helperText="Separate multiple consultation types with commas"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Availability Days"
              value={formData.practiceSetup.availabilityDays.join(', ')}
              onChange={(e) => handleInputChange('practiceSetup', 'availabilityDays', e.target.value.split(',').map(d => d.trim()))}
              disabled={!editMode}
              helperText="Separate multiple days with commas"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Services Offered"
              value={formData.practiceSetup.services.join(', ')}
              onChange={(e) => handleInputChange('practiceSetup', 'services', e.target.value.split(',').map(s => s.trim()))}
              disabled={!editMode}
              helperText="Separate multiple services with commas"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Professional Summary"
              multiline
              rows={4}
              value={formData.practiceSetup.professionalSummary}
              onChange={(e) => handleInputChange('practiceSetup', 'professionalSummary', e.target.value)}
              disabled={!editMode}
              helperText="Write a brief summary about your practice and approach"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Website"
              value={formData.practiceSetup.website}
              onChange={(e) => handleInputChange('practiceSetup', 'website', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  const renderPaymentSetup = () => (
    <StyledCard>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Payment Setup</Typography>
          <Button
            startIcon={editMode ? <Save /> : <Edit />}
            onClick={() => editMode ? handleSave('paymentSetup') : setEditMode(true)}
          >
            {editMode ? 'Save' : 'Edit'}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Bank Name"
              value={formData.paymentSetup.bankName}
              onChange={(e) => handleInputChange('paymentSetup', 'bankName', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Account Holder Name"
              value={formData.paymentSetup.accountHolderName}
              onChange={(e) => handleInputChange('paymentSetup', 'accountHolderName', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Account Number"
              value={formData.paymentSetup.accountNumber}
              onChange={(e) => handleInputChange('paymentSetup', 'accountNumber', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="IFSC Code"
              value={formData.paymentSetup.ifscCode}
              onChange={(e) => handleInputChange('paymentSetup', 'ifscCode', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PAN Number"
              value={formData.paymentSetup.panNumber}
              onChange={(e) => handleInputChange('paymentSetup', 'panNumber', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="GST Number"
              value={formData.paymentSetup.gstNumber}
              onChange={(e) => handleInputChange('paymentSetup', 'gstNumber', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Cycle"
              value={formData.paymentSetup.paymentCycle}
              onChange={(e) => handleInputChange('paymentSetup', 'paymentCycle', e.target.value)}
              disabled={!editMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Minimum Payout"
              type="number"
              value={formData.paymentSetup.minimumPayout}
              onChange={(e) => handleInputChange('paymentSetup', 'minimumPayout', e.target.value)}
              disabled={!editMode}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  // Plan selection handler
  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanSummaryDialog(true);
    
    // Track plan selection for analytics
    if (window.gtag) {
      window.gtag('event', 'select_plan', {
        plan_name: plan.name,
        plan_price: plan.price,
        plan_type: plan.plan_type
      });
    }
  };

  // Payment handler with status dialogs
  const handleRazorpayPayment = async (plan) => {
    setIsProcessingPayment(true);
    setPaymentStatus('processing');
    setPlanSummaryDialog(false);
    try {
      const response = await axiosInstance.post('/payments/subscribe/', { plan_id: plan.id });
      const { subscription_id, razorpay_key } = response.data;
      const options = {
        key: razorpay_key,
        subscription_id: subscription_id,
        name: 'MindWell',
        description: `Subscription for ${plan.name}`,
        handler: async (rzpResponse) => {
          await fetchCurrentSubscription();
          setIsProcessingPayment(false);
          setPaymentStatus('success');
          setPaymentDetails({ plan, rzpResponse });
          enqueueSnackbar('Payment successful! Subscription activated.', { variant: 'success' });
        },
        prefill: {
          name: profileData?.full_name || '',
          email: profileData?.email || '',
          contact: profileData?.phone || '',
        },
        theme: { color: '#1976d2' },
        modal: { ondismiss: () => setIsProcessingPayment(false) },
      };
      const rzp = new Razorpay(options);
      rzp.open();
    } catch (error) {
      setIsProcessingPayment(false);
      setPaymentStatus('failure');
      enqueueSnackbar('Failed to initiate payment. Please try again.', { variant: 'error' });
    }
  };

  const renderSubscriptionManagement = () => {
    // Use data from our new subscription context
    const isPaidSubscription = hasActiveSubscription();
    const isTrialActive = isOnTrial();
    const isExpired = !hasActiveSubscription() && !isOnTrial();
    const isCancelled = subscriptionStatus?.status?.status === 'cancelled';

    // Show loading state
    if (subscriptionLoading || isLoadingPlans) {
      return (
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="400px"
          gap={2}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading subscription plans...
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Enhanced Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                color: 'text.primary',
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Subscription Management
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontWeight: 400 }}>
              Choose the perfect plan for your practice needs and unlock premium features
            </Typography>
            
            {/* Quick Stats */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {plans.length}
                    </Typography>
                    <Typography variant="body2">Available Plans</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {isTrialActive ? getDaysRemaining() : 0}
                    </Typography>
                    <Typography variant="body2">Trial Days Left</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ 
                  background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                  color: 'white',
                  borderRadius: 3
                }}>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {isPaidSubscription ? getDaysRemaining() : 0}
                    </Typography>
                    <Typography variant="body2">Subscription Days</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Enhanced Status Banners */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Trial Banner */}
          {isTrialActive && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                    🎉 Free Trial Active
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 1 }}>
                    You have <strong>{getDaysRemaining()} days</strong> remaining in your trial
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                    Trial ends on {subscriptionStatus?.status?.end_date ? new Date(subscriptionStatus.status.end_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <LinearProgress 
                    variant="determinate" 
                    value={trialStatus.progress_percentage || 0}
                    sx={{ 
                      width: 200, 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'white', opacity: 0.8 }}>
                    {trialStatus.progress_percentage || 0}% used
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}

          {/* Active Subscription Banner */}
          {isPaidSubscription && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3)'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                    ✅ Active Subscription
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 1 }}>
                    You're subscribed to <strong>{getPlanType()}</strong> plan
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                    Next billing: {subscriptionStatus?.status?.end_date ? new Date(subscriptionStatus.status.end_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      borderColor: 'white', 
                      color: 'white',
                      '&:hover': { 
                        borderColor: 'white', 
                        backgroundColor: 'rgba(255,255,255,0.1)' 
                      }
                    }}
                    onClick={() => setSubscriptionView('billing')}
                  >
                    View Billing
                  </Button>
                  <Button 
                    variant="contained" 
                    sx={{ 
                      backgroundColor: 'white', 
                      color: '#4caf50',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                    }}
                    onClick={() => setSubscriptionView('plans')}
                  >
                    Manage Plan
                  </Button>
                </Box>
              </Box>
            </Alert>
          )}

          {/* Expired Subscription Banner */}
          {isExpired && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                    ⚠️ Subscription Expired
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9 }}>
                    Your subscription has expired. Renew to continue accessing premium features.
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  sx={{ 
                    backgroundColor: 'white', 
                    color: '#f44336',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                  }}
                  onClick={() => setSubscriptionView('plans')}
                >
                  Renew Now
                </Button>
              </Box>
            </Alert>
          )}

          {/* Cancelled Subscription Banner */}
          {isCancelled && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(237, 108, 2, 0.3)'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                    ⏸️ Subscription Cancelled
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9 }}>
                    Your subscription has been cancelled. You can reactivate anytime.
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  sx={{ 
                    backgroundColor: 'white', 
                    color: '#ed6c02',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                  }}
                  onClick={() => handleResumeSubscription()}
                >
                  Reactivate
                </Button>
              </Box>
            </Alert>
          )}

          {/* No Active Plan Alert */}
          {!isPaidSubscription && !isTrialActive && !isExpired && !isCancelled && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: 3,
                background: 'linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(237, 108, 2, 0.3)'
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                    ⚠️ No Active Plan
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'white', opacity: 0.9 }}>
                    Subscribe to continue using premium features and unlock your practice's full potential
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  sx={{ 
                    backgroundColor: 'white', 
                    color: '#ed6c02',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                  }}
                  onClick={() => setSubscriptionView('plans')}
                >
                  Subscribe Now
                </Button>
              </Box>
            </Alert>
          )}
        </motion.div>

        {/* Enhanced Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Paper sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
            <Tabs
              value={subscriptionView}
              onChange={(e, newValue) => setSubscriptionView(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  py: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '1rem'
                },
                '& .Mui-selected': {
                  color: theme.palette.primary.main
                }
              }}
            >
              <Tab 
                value="plans" 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Store />
                    Plans & Pricing
                  </Box>
                } 
              />
              <Tab 
                value="billing" 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Receipt />
                    Billing History
                  </Box>
                } 
              />
              <Tab 
                value="faq" 
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Info />
                    FAQ & Support
                  </Box>
                } 
              />
            </Tabs>
          </Paper>
        </motion.div>

        {/* Content Based on Selected Tab */}
        {subscriptionView === 'plans' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {/* Enhanced Plans Grid */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
                Choose Your Perfect Plan
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Select the plan that best fits your practice needs and scale as you grow
              </Typography>
              
              <Grid container spacing={4}>
                {plans.map((plan, index) => {
                  const isCurrent = isPaidSubscription && subscription?.plan?.id === plan.id;
                  const isEnterprise = plan.plan_type === 'enterprise';
                  const isPremium = plan.plan_type === 'professional';
                  const isBasic = plan.plan_type === 'basic';
                
                  return (
                    <Grid item xs={12} md={4} key={plan.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 * index }}
                      >
                        <Card
                          sx={{
                            height: '100%',
                            border: isCurrent 
                              ? `3px solid ${theme.palette.success.main}` 
                              : isPremium 
                              ? `3px solid ${theme.palette.secondary.main}` 
                              : isBasic
                              ? `3px solid ${theme.palette.primary.main}`
                              : '1px solid rgba(0,0,0,0.1)',
                            borderRadius: 4,
                            boxShadow: isCurrent ? 12 : isPremium ? 8 : isBasic ? 6 : 4,
                            position: 'relative',
                            background: isCurrent 
                              ? `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`
                              : isPremium 
                              ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`
                              : isBasic
                              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`
                              : undefined,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: isCurrent ? 16 : isPremium ? 12 : isBasic ? 10 : 8,
                            }
                          }}
                          onClick={() => handleSelectPlan(plan)}
                        >
                          {/* Popular Badge */}
                          {isPremium && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)',
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 3,
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                zIndex: 1,
                                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)'
                              }}
                            >
                              ⭐ MOST POPULAR
                            </Box>
                          )}

                          {/* Current Plan Badge */}
                          {isCurrent && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                backgroundColor: theme.palette.success.main,
                                color: 'white',
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                zIndex: 1,
                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                              }}
                            >
                              ✓ CURRENT
                            </Box>
                          )}

                          <CardContent sx={{ p: 4 }}>
                            {/* Plan Header */}
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {plan.name}
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="h2" color="primary" sx={{ fontWeight: 800, mb: 0 }}>
                                  {isEnterprise ? 'Custom' : `₹${plan.price}`}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  {isEnterprise ? 'Contact for pricing' : 'per month'}
                                </Typography>
                              </Box>
                              {isPremium && (
                                <Chip 
                                  label="Save 20% annually" 
                                  color="secondary" 
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </Box>

                            {/* Features List */}
                            <Box sx={{ mb: 4 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                                What's included:
                              </Typography>
                              <List dense sx={{ p: 0 }}>
                                {plan.features.map((feature, idx) => (
                                  <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CheckCircle 
                                        color="success" 
                                        sx={{ fontSize: 22 }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={feature} 
                                      primaryTypographyProps={{ 
                                        variant: 'body1',
                                        sx: { fontWeight: 500 }
                                      }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>

                            {/* Action Button */}
                            <Box sx={{ mt: 'auto' }}>
                              {isEnterprise ? (
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  color="primary"
                                  size="large"
                                  href="mailto:support@mindwell.com?subject=Enterprise%20Plan%20Enquiry"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    py: 2,
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    fontSize: '1.1rem'
                                  }}
                                >
                                  📧 Contact Support
                                </Button>
                              ) : isCurrent ? (
                                <Button
                                  fullWidth
                                  variant="contained"
                                  color="success"
                                  size="large"
                                  disabled
                                  sx={{ 
                                    py: 2,
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    fontSize: '1.1rem'
                                  }}
                                >
                                  ✓ Current Plan
                                </Button>
                              ) : (
                                <Button
                                  fullWidth
                                  variant="contained"
                                  color={isPremium ? 'secondary' : isBasic ? 'primary' : 'primary'}
                                  size="large"
                                  onClick={() => {
                                    if (isPaidSubscription) {
                                      handleUpgradeSubscription(plan.id);
                                    } else {
                                      handleSelectPlan(plan);
                                    }
                                  }}
                                  sx={{ 
                                    py: 2,
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    fontSize: '1.1rem',
                                    background: isPremium 
                                      ? 'linear-gradient(135deg, #9c27b0 0%, #673ab7 100%)'
                                      : isBasic
                                      ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                                      : undefined,
                                    boxShadow: isPremium 
                                      ? '0 4px 12px rgba(156, 39, 176, 0.4)'
                                      : isBasic
                                      ? '0 4px 12px rgba(25, 118, 210, 0.4)'
                                      : undefined,
                                    '&:hover': {
                                      boxShadow: isPremium 
                                        ? '0 6px 16px rgba(156, 39, 176, 0.6)'
                                        : isBasic
                                        ? '0 6px 16px rgba(25, 118, 210, 0.6)'
                                        : undefined,
                                    }
                                  }}
                                >
                                  {isPaidSubscription ? 'Upgrade Plan' : 'Start Plan'}
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Plan Comparison Section */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                  Compare Plans
                </Typography>
                <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Features</TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan.id} sx={{ fontWeight: 700, textAlign: 'center' }}>
                              {plan.name}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Monthly Price</TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan.id} sx={{ textAlign: 'center', fontWeight: 600 }}>
                              ₹{plan.price}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Patient Management</TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan.id} sx={{ textAlign: 'center' }}>
                              {plan.features.includes('Unlimited patients') ? '✓' : '✗'}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Advanced Analytics</TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan.id} sx={{ textAlign: 'center' }}>
                              {plan.features.includes('Advanced analytics') ? '✓' : '✗'}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Priority Support</TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan.id} sx={{ textAlign: 'center' }}>
                              {plan.features.includes('Priority support') ? '✓' : '✗'}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            </Box>
          </motion.div>
        )}

        {subscriptionView === 'billing' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {/* Enhanced Billing History Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Billing History
              </Typography>
              
              {/* Billing Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        ₹{subscription?.plan?.price || 0}
                      </Typography>
                      <Typography variant="body2">Monthly Cost</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {subscription?.end_date ? new Date(subscription.end_date).toLocaleDateString() : 'N/A'}
                      </Typography>
                      <Typography variant="body2">Next Billing Date</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                    color: 'white',
                    borderRadius: 3
                  }}>
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {subscription?.auto_renew ? 'Yes' : 'No'}
                      </Typography>
                      <Typography variant="body2">Auto Renewal</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Invoice</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                              <ReceiptLong sx={{ fontSize: 48, color: 'text.secondary' }} />
                              <Typography variant="h6" color="text.secondary">
                                No billing history available
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Your billing history will appear here once you have active subscriptions
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </motion.div>
        )}

        {subscriptionView === 'faq' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {/* Enhanced FAQ Section */}
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Frequently Asked Questions
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Can I change my plan anytime?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and we'll prorate any differences in billing.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        What payment methods do you accept?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We accept all major credit cards, debit cards, UPI, net banking, and digital wallets through our secure Razorpay integration.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Is there a free trial?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yes! New users get a 14-day free trial with access to all Premium features. No credit card required to start your trial.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Can I cancel anytime?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Absolutely! You can cancel your subscription at any time with no cancellation fees. You'll continue to have access until the end of your billing period.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        What happens if I exceed my plan limits?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We'll notify you when you're approaching your limits and offer you the option to upgrade to a higher plan to continue without interruption.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 3, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 } }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Do you offer annual billing?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Yes! Annual plans offer significant savings (up to 20% off) compared to monthly billing. You can switch to annual billing anytime.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Contact Support Section */}
              <Box sx={{ mt: 6, textAlign: 'center' }}>
                <Card sx={{ 
                  borderRadius: 3, 
                  background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                  p: 4
                }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    Still have questions?
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Our support team is here to help you get the most out of MindWell
                  </Typography>
                  <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      href="mailto:support@mindwell.com"
                      startIcon={<Info />}
                      sx={{ borderRadius: 3, px: 4, py: 1.5 }}
                    >
                      Contact Support
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="large"
                      href="/help"
                      startIcon={<Description />}
                      sx={{ borderRadius: 3, px: 4, py: 1.5 }}
                    >
                      Help Center
                    </Button>
                  </Box>
                </Card>
              </Box>
            </Box>
          </motion.div>
        )}
      </Box>
    );
  };

  const renderNotificationSettings = () => (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>Notification Preferences</Typography>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.emailNotifications}
                onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
              />
            }
            label="Email Notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.smsNotifications}
                onChange={(e) => handleInputChange('notifications', 'smsNotifications', e.target.checked)}
              />
            }
            label="SMS Notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.appointmentReminders}
                onChange={(e) => handleInputChange('notifications', 'appointmentReminders', e.target.checked)}
              />
            }
            label="Appointment Reminders"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.notifications.marketingEmails}
                onChange={(e) => handleInputChange('notifications', 'marketingEmails', e.target.checked)}
              />
            }
            label="Marketing Emails"
          />
        </Stack>
      </CardContent>
    </StyledCard>
  );

  const renderSecuritySettings = () => (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>Security Settings</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Current Password"
              value={formData.security.currentPassword}
              onChange={(e) => handleInputChange('security', 'currentPassword', e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password"
              value={formData.security.newPassword}
              onChange={(e) => handleInputChange('security', 'newPassword', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="Confirm New Password"
              value={formData.security.confirmPassword}
              onChange={(e) => handleInputChange('security', 'confirmPassword', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.security.twoFactorAuth}
                  onChange={(e) => handleInputChange('security', 'twoFactorAuth', e.target.checked)}
                />
              }
              label="Enable Two-Factor Authentication"
            />
          </Grid>
        </Grid>
      </CardContent>
    </StyledCard>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Settings</Typography>
      {renderProfileCompletion()}
      
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<AccountCircle />} label="Profile" />
          <Tab icon={<Business />} label="Clinic" />
          <Tab icon={<Description />} label="Documents" />
          <Tab icon={<Store />} label="Practice & Payment" />
          <Tab icon={<CardMembership />} label="Subscription" />
          <Tab icon={<Notifications />} label="Notifications" />
          <Tab icon={<Security />} label="Security" />
        </Tabs>
      </Paper>

      {(profileLoading || clinicLoading) ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && <ProfileTab />}
          {activeTab === 1 && <ClinicTab />}
          {activeTab === 2 && <DocumentsTab />}
          {activeTab === 3 && <PracticePaymentTab />}
          {activeTab === 4 && (
            <Box>
              {/* Always show subscription information regardless of authentication */}
              {!isAuthenticated ? (
                <Card sx={{ 
                  border: '2px solid', 
                  borderColor: 'warning.main',
                  background: alpha(theme.palette.warning.main, 0.05)
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Lock sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                      Authentication Required
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Please log in to view and manage your subscription information.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="warning"
                      onClick={() => navigate('/login')}
                      startIcon={<Security />}
                      size="large"
                    >
                      Login Now
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Enhanced Subscription Status Banner */}
                  {subscriptionError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {subscriptionError}
                      <Button 
                        variant="link" 
                        onClick={refreshSubscription}
                        sx={{ ml: 2 }}
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {/* Usage Overview Cards */}
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Appointments</Typography>
                          <Typography variant="h4" color="primary">
                            {getCurrentUsage('appointments')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Used this month
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={getUsagePercentage('appointments')}
                            sx={{ mt: 1 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Patients</Typography>
                          <Typography variant="h4" color="primary">
                            {getCurrentUsage('patients')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total patients
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={getUsagePercentage('patients')}
                            sx={{ mt: 1 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Consultations</Typography>
                          <Typography variant="h4" color="primary">
                            {getCurrentUsage('consultations')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            This month
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={getUsagePercentage('consultations')}
                            sx={{ mt: 1 }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Plan Status</Typography>
                          <Typography variant="h4" color="primary">
                            {getPlanType() || 'None'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {isOnTrial() ? 'Trial Active' : hasActiveSubscription() ? 'Active' : 'Expired'}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Chip 
                              label={isOnTrial() ? 'Trial' : hasActiveSubscription() ? 'Active' : 'Expired'}
                              color={isOnTrial() ? 'warning' : hasActiveSubscription() ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Enhanced Subscription Management */}
                  <SubscriptionManagement />
                </>
              )}
            </Box>
          )}
          {activeTab === 5 && <NotificationsTab />}
          {activeTab === 6 && <SecurityTab />}
        </Box>
      )}

      {/* Subscription Dialog */}
      <Dialog
        open={subscriptionDialog}
        onClose={() => setSubscriptionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star color="primary" />
            Choose Your Plan
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: selectedPlan?.id === plan.id ? `2px solid ${theme.palette.primary.main}` : undefined,
                    cursor: 'pointer',
                    '&:hover': {
                      border: `2px solid ${theme.palette.primary.main}`,
                    },
                  }}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      ₹{plan.price}/month
                    </Typography>
                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <Box sx={{ p: 2, mt: 'auto' }}>
                    <Button
                      fullWidth
                      variant={selectedPlan?.id === plan.id ? 'contained' : 'outlined'}
                      onClick={() => {
                        // Handle plan selection
                        setSelectedPlan(plan);
                      }}
                    >
                      {selectedPlan?.id === plan.id ? 'Selected' : 'Select Plan'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedPlan}
            onClick={handleUpgradePlan}
          >
            Confirm Upgrade
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Method Dialog */}
      <Dialog
        open={paymentMethodDialog}
        onClose={() => setPaymentMethodDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CreditCard color="primary" />
            Add Payment Method
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Card Number"
              placeholder="1234 5678 9012 3456"
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiry Date"
                  placeholder="MM/YY"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="CVV"
                  placeholder="123"
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Set as default payment method"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
        <Button
          startIcon={<Cancel />}
          color="error"
          variant="outlined"
          size="small"
          onClick={handleCancelSubscription}
          disabled={subscription?.status === 'cancelled'}
        >
          Cancel
        </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Handle adding payment method
              setPaymentMethodDialog(false);
            }}
          >
            Add Card
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Plan Summary Dialog */}
      <Dialog 
        open={planSummaryDialog} 
        onClose={() => setPlanSummaryDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          pb: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Star sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Confirm Your Plan
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Review your selection before proceeding to payment
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {selectedPlan && (
            <Box>
              {/* Plan Summary Card */}
              <Card sx={{ 
                mb: 3, 
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 3,
                background: alpha(theme.palette.primary.main, 0.05)
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {selectedPlan.name}
                    </Typography>
                    <Chip 
                      label="Selected Plan" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 800, mb: 1 }}>
                    ₹{selectedPlan.price}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    per month • Billed monthly
                  </Typography>
                  
                  {/* Features Preview */}
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                    What's included:
                  </Typography>
                  <List dense sx={{ p: 0 }}>
                    {selectedPlan.features.slice(0, 5).map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                    {selectedPlan.features.length > 5 && (
                      <ListItem sx={{ px: 0, py: 0.5 }}>
                        <ListItemText 
                          primary={`+${selectedPlan.features.length - 5} more features`}
                          primaryTypographyProps={{ 
                            variant: 'body2', 
                            color: 'primary.main',
                            fontWeight: 600
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>

              {/* Payment Security Notice */}
              <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Security sx={{ fontSize: 20 }} />
                  <Typography variant="body2">
                    Your payment is secured by Razorpay with bank-level encryption. We never store your card details.
                  </Typography>
                </Box>
              </Alert>

              {/* Terms and Conditions */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  By proceeding, you agree to our{' '}
                  <Typography 
                    component="span" 
                    variant="body2" 
                    color="primary" 
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Terms of Service
                  </Typography>
                  {' '}and{' '}
                  <Typography 
                    component="span" 
                    variant="body2" 
                    color="primary" 
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Privacy Policy
                  </Typography>
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setPlanSummaryDialog(false)}
            variant="outlined"
            size="large"
            sx={{ borderRadius: 2, px: 4 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleRazorpayPayment(selectedPlan)} 
            disabled={isProcessingPayment}
            size="large"
            sx={{ 
              borderRadius: 2, 
              px: 4,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
              }
            }}
          >
            {isProcessingPayment ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="inherit" />
                Processing...
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Payment />
                Proceed to Payment
              </Box>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Payment Status Dialog */}
      <Dialog 
        open={!!paymentStatus} 
        onClose={() => setPaymentStatus(null)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: paymentStatus === 'success' 
            ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
            : paymentStatus === 'failure'
            ? 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)'
            : 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
          color: 'white',
          pb: 3
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            {paymentStatus === 'success' && <CheckCircle sx={{ fontSize: 28 }} />}
            {paymentStatus === 'failure' && <Warning sx={{ fontSize: 28 }} />}
            {paymentStatus === 'processing' && <CircularProgress size={28} color="inherit" />}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {paymentStatus === 'success' ? 'Payment Successful!' : 
                 paymentStatus === 'failure' ? 'Payment Failed' : 
                 'Processing Payment...'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {paymentStatus === 'success' ? 'Your subscription has been activated' : 
                 paymentStatus === 'failure' ? 'We couldn\'t process your payment' : 
                 'Please wait while we process your payment'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {paymentStatus === 'processing' && (
            <Box textAlign="center" py={4}>
              <CircularProgress size={64} sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom>
                Processing your payment...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please don't close this window. This may take a few moments.
              </Typography>
            </Box>
          )}
          
          {paymentStatus === 'success' && paymentDetails && (
            <Box textAlign="center" py={2}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <CheckCircle sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'success.main' }}>
                Welcome to {paymentDetails.plan.name}!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your subscription has been successfully activated
              </Typography>
              
              {/* Success Details Card */}
              <Card sx={{ 
                background: alpha(theme.palette.success.main, 0.1),
                border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                borderRadius: 3,
                mb: 3
              }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Plan</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {paymentDetails.plan.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ₹{paymentDetails.plan.price}/month
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Next Billing</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {subscription?.end_date ? new Date(subscription.end_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip 
                        label="Active" 
                        color="success" 
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              <Typography variant="body2" color="text.secondary">
                You can now access all premium features. We've sent a confirmation email with your receipt.
              </Typography>
            </Box>
          )}
          
          {paymentStatus === 'failure' && (
            <Box textAlign="center" py={2}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3
              }}>
                <Warning sx={{ fontSize: 48, color: 'white' }} />
              </Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
                Payment Failed
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                We couldn't process your payment. This could be due to insufficient funds or card restrictions.
              </Typography>
              
              {/* Error Details Card */}
              <Card sx={{ 
                background: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 3,
                mb: 3
              }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    What you can do:
                  </Typography>
                  <List dense sx={{ p: 0 }}>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle color="success" sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Check your card details and try again" 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle color="success" sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Ensure sufficient funds in your account" 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle color="success" sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Contact your bank if the issue persists" 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
              
              <Typography variant="body2" color="text.secondary">
                Need help? Contact our support team for assistance.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          {paymentStatus === 'success' && (
            <>
              <Button 
                variant="outlined"
                size="large"
                startIcon={<Download />}
                sx={{ borderRadius: 2, px: 4 }}
              >
                Download Receipt
              </Button>
              <Button 
                variant="contained"
                size="large"
                onClick={() => setPaymentStatus(null)}
                sx={{ 
                  borderRadius: 2, 
                  px: 4,
                  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
                }}
              >
                Get Started
              </Button>
            </>
          )}
          {paymentStatus === 'failure' && (
            <>
              <Button 
                variant="outlined"
                size="large"
                startIcon={<Support />}
                href="mailto:support@mindwell.com"
                sx={{ borderRadius: 2, px: 4 }}
              >
                Contact Support
              </Button>
              <Button 
                variant="contained"
                size="large"
                onClick={() => setPaymentStatus(null)}
                sx={{ 
                  borderRadius: 2, 
                  px: 4,
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
                }}
              >
                Try Again
              </Button>
            </>
          )}
          {paymentStatus === 'processing' && (
            <Button 
              variant="outlined"
              size="large"
              onClick={() => setPaymentStatus(null)}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings; 